import React, { useState, useEffect, useMemo, useRef } from 'react';
import type {
  Order,
  SmartpayTerminal,
  Payment,
  Customer,
  IntegrationSettings,
  PaymentItemAllocation,
  SubBill,
} from '../types';
import { sendPaymentToTerminal } from '../lib/smartconnect';
import CreditCardIcon from '../components/icons/CreditCardIcon';
import CashIcon from '../components/icons/CashIcon';
import ColumnsIcon from '../components/icons/ColumnsIcon';
import BankBuildingIcon from '../components/icons/BankBuildingIcon';
import UsersIcon from '../components/icons/UsersIcon';
import CheckCircleIcon from '../components/icons/CheckCircleIcon';
import XCircleIcon from '../components/icons/XCircleIcon';
import ArrowPathIcon from '../components/icons/ArrowPathIcon';
import PrinterIcon from '../components/icons/PrinterIcon';
import { useSoftKeyboard } from '../contexts/SoftKeyboardContext';
import TipInput from '../components/payments/TipInput';
import { getOrderTotalFromItems, getAmountPaid, orderHasMeaningfulItems } from '../lib/orderUtils';
import ReceiptQrModal from '../components/pos/ReceiptQrModal';
import SubBillBuilder, { type SubBillBuilderResult } from '../components/pos/SubBillBuilder';

/** Preview shape used when the cashier asks to print a sub-bill before paying for it. */
export interface PendingSubBillPreview {
  name: string;
  total: number;
  allocations: PaymentItemAllocation[];
  discountAllocationMode: 'proportional' | 'remainder';
}

interface PaymentModalProps {
  order: Order;
  customer: Customer | null;
  terminals: SmartpayTerminal[];
  /** One-line warning when Event terminal filter fell back to all paired devices */
  terminalSelectionNotice?: string;
  onClose: () => void;
  onProcessPayment: (orderId: string, payment: Payment) => void;
  onPrintReceipt: (order: Order) => void;
  onNewOrder: () => void;
  /** When order is fully paid (payments sum >= total) but paymentStatus is still 'unpaid', call to sync Firestore so order leaves Pending and table is freed */
  onSyncPaymentStatus?: (orderId: string) => void;
  /** For event/stall orders: mark order as completed (served) after payment */
  onMarkOrderCompleted?: (order: Order) => void | Promise<void>;
  /** Event queue token shown on payment success (Mobile POS), e.g. "Token #12" or "Token L3" */
  eventTokenDisplay?: string;
  integrationSettings?: IntegrationSettings;
  /** Mobile POS: void unpaid event ticket (marks order cancelled in Firestore) */
  onCancelUnpaidOrder?: () => void | Promise<void>;
  /** Print a draft sub-bill receipt (before payment). When omitted, the "Print bill only" button is hidden. */
  onPrintSubBillPreview?: (order: Order, preview: PendingSubBillPreview) => void;
  /** Reprint a closed sub-bill from order.subBills. When omitted, reprint buttons are hidden. */
  onReprintSubBill?: (order: Order, subBill: SubBill) => void;
}

type PaymentStatus = 'idle' | 'processing' | 'approved' | 'declined';
type PaymentTab = 'card' | 'cash' | 'bank' | 'split' | 'items';

/** Stable id for a sub-bill — uses crypto.randomUUID when available, falls back to ts+random. */
function generateSubBillId(): string {
  if (typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function') {
    return `sb_${(crypto as Crypto).randomUUID()}`;
  }
  return `sb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
}

/** Stable id for a Payment — same fallback strategy as sub-bills. */
function generatePaymentId(): string {
  if (typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function') {
    return `pay_${(crypto as Crypto).randomUUID()}`;
  }
  return `pay_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ order, customer, terminals, terminalSelectionNotice, onClose, onProcessPayment, onPrintReceipt, onNewOrder, onSyncPaymentStatus, onMarkOrderCompleted, eventTokenDisplay, integrationSettings, onCancelUnpaidOrder, onPrintSubBillPreview, onReprintSubBill }) => {
  // Sub-bill (split-by-item) is unavailable for buffet (per cover) and aggregator (external payment) orders.
  const subBillsAllowed = order.orderType !== 'buffet' && order.orderType !== 'uber-doordash';
  const [activeTab, setActiveTab] = useState<PaymentTab>('card');
  const hasSyncedPaymentStatusRef = useRef(false);
  /** Keeps the last good order snapshot so live listener updates cannot zero out amount due mid-payment. */
  const paymentOrderSnapshotRef = useRef(order);

  useEffect(() => {
    paymentOrderSnapshotRef.current = order;
    hasSyncedPaymentStatusRef.current = false;
  }, [order.id]);

  /**
   * When set, the cashier has built a sub-bill in the Items tab and clicked "Pay $X →".
   * We then switch to a payment-method tab (Card/Cash/Bank) with the amount pre-filled and
   * locked. Any payment posted while staged is tagged with the sub-bill metadata.
   */
  const [stagedSubBill, setStagedSubBill] = useState<SubBillBuilderResult | null>(null);
  const stagedSubBillIdRef = useRef<string | null>(null);
  if (stagedSubBill && !stagedSubBillIdRef.current) {
    stagedSubBillIdRef.current = generateSubBillId();
  }
  if (!stagedSubBill && stagedSubBillIdRef.current) {
    stagedSubBillIdRef.current = null;
  }

  const [cashTenderedInput, setCashTenderedInput] = useState('');
  const [selectedTerminalId, setSelectedTerminalId] = useState<string>(terminals[0]?.id || '');

  useEffect(() => {
    setSelectedTerminalId((prev) => (terminals.some((t) => t.id === prev) ? prev : terminals[0]?.id || ''));
  }, [terminals]);
  const [cardStatus, setCardStatus] = useState<PaymentStatus>('idle');
  const [cardStatusMessage, setCardStatusMessage] = useState('');
  const [splitCashAmountInput, setSplitCashAmountInput] = useState('');
  const [cardAmountInput, setCardAmountInput] = useState('');
  const [email, setEmail] = useState(customer?.email || '');
  const [emailSent, setEmailSent] = useState(false);
  const [showReceiptQr, setShowReceiptQr] = useState(false);
  const [cardTipAmount, setCardTipAmount] = useState(0);
  const { enabled: softKeyboardEnabled, openKeyboard, closeKeyboard } = useSoftKeyboard();

  // Dismiss any open soft keyboard immediately so its panel cannot overlay the payment tabs.
  useEffect(() => { closeKeyboard(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefer live payments but keep a stable item/total snapshot so amount due does not flicker to $0.
  const paymentOrder = useMemo(() => {
    const live = order;
    const snap = paymentOrderSnapshotRef.current;
    let base: Order;
    if (orderHasMeaningfulItems(live)) {
      paymentOrderSnapshotRef.current = live;
      base = live;
    } else if (orderHasMeaningfulItems(snap)) {
      base = snap;
    } else {
      const liveTotal = getOrderTotalFromItems(live) ?? live.totalAmount ?? 0;
      const snapTotal = getOrderTotalFromItems(snap) ?? snap.totalAmount ?? 0;
      base = snapTotal > liveTotal + 0.01 ? snap : live;
    }
    return {
      ...base,
      payments: order.payments ?? base.payments,
      paymentStatus: order.paymentStatus ?? base.paymentStatus,
    };
  }, [order]);

  // Use computed total from items (matches receipt) so balance is correct even if order.totalAmount is stale.
  // Fall back to paymentOrder.totalAmount when items are absent (e.g. first render before snapshot stabilises)
  // so amountDue never incorrectly becomes 0 and forces the success screen prematurely.
  const computedTotal = getOrderTotalFromItems(paymentOrder);
  const orderTotal = computedTotal > 0.005 ? computedTotal : (paymentOrder.totalAmount ?? 0);
  const totalPaid = getAmountPaid(paymentOrder);
  const amountDue = Math.round((orderTotal - totalPaid) * 100) / 100;
  const isFullyPaidButUnsynced =
    orderTotal > 0.01 &&
    amountDue <= 0.01 &&
    order.paymentStatus !== 'paid' &&
    totalPaid > 0.01;

  // Sync Firestore when payments show full payment but document still has paymentStatus 'unpaid' (fixes stuck Pending / table occupied)
  useEffect(() => {
    if (isFullyPaidButUnsynced && onSyncPaymentStatus && !hasSyncedPaymentStatusRef.current) {
      hasSyncedPaymentStatusRef.current = true;
      onSyncPaymentStatus(order.id);
    }
  }, [isFullyPaidButUnsynced, onSyncPaymentStatus, order.id]);

  const parseAmountInput = (value: string) => {
    if (!value) return 0;
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  /**
   * Returns the fields that must be merged into a Payment object when a sub-bill is staged.
   * The actual paidQuantity bookkeeping happens server-side in handleProcessPayment (App.tsx).
   */
  const subBillPaymentMeta = (): Pick<
    Payment,
    'subBillId' | 'subBillName' | 'discountAllocationMode' | 'itemAllocations'
  > | null => {
    if (!stagedSubBill || !stagedSubBillIdRef.current) return null;
    return {
      subBillId: stagedSubBillIdRef.current,
      subBillName: stagedSubBill.customerName || undefined,
      discountAllocationMode: stagedSubBill.discountAllocationMode,
      itemAllocations: stagedSubBill.allocations,
    };
  };

  const closeStagedSubBill = () => {
    setStagedSubBill(null);
    setCardAmountInput('');
    setCashTenderedInput('');
    setSplitCashAmountInput('');
    setCardTipAmount(0);
  };

  const cashTenderedValue = parseAmountInput(cashTenderedInput);
  const splitCashAmountValue = parseAmountInput(splitCashAmountInput);
  const cardAmountValueRaw = cardAmountInput ? parseAmountInput(cardAmountInput) : amountDue;
  const cardAmountValue = Math.round(cardAmountValueRaw * 100) / 100;

  /**
   * When a sub-bill is staged, the payment amount is fixed = stagedSubBill.total.
   * The Card/Cash/Bank tabs lock their amount inputs to this value.
   */
  const stagedTotal = stagedSubBill?.total ?? 0;
  const effectivePayAmount = stagedSubBill ? stagedTotal : amountDue;

  // Pre-fill cash tender so Confirm is ready without tapping Exact first
  useEffect(() => {
    if (activeTab !== 'cash' || amountDue <= 0) return;
    setCashTenderedInput((prev) => {
      if (prev.trim()) return prev;
      const amt = stagedSubBill ? stagedTotal : amountDue;
      return amt > 0 ? amt.toFixed(2) : prev;
    });
  }, [activeTab, amountDue, stagedSubBill, stagedTotal]);

  const handleCashPayment = () => {
    // If amount due is 0 or less, order is already paid
    if (amountDue <= 0) {
      console.log('💳 Amount due is 0, order already paid');
      return;
    }

    const tendered = Math.round(cashTenderedValue * 100) / 100;
    if (isNaN(tendered) || tendered <= 0) {
      alert("Please enter a valid amount greater than $0.00.");
      return;
    }
    // When a sub-bill is staged, the cashier must collect AT LEAST the sub-bill total in cash;
    // the recorded payment is exactly the sub-bill total (any extra is change handed back).
    // Otherwise allow partial cash payments (existing behaviour).
    const targetAmount = stagedSubBill ? stagedTotal : amountDue;
    if (stagedSubBill && tendered + 0.005 < stagedTotal) {
      alert(`Cash tendered ($${tendered.toFixed(2)}) is less than the sub-bill total ($${stagedTotal.toFixed(2)}).`);
      return;
    }
    const paymentAmount = Math.min(tendered, targetAmount);
    const payment: Payment = {
      id: generatePaymentId(),
      method: 'cash',
      amount: Math.round(paymentAmount * 100) / 100,
      timestamp: Date.now(),
      ...(subBillPaymentMeta() ?? {}),
    };
    onProcessPayment(order.id, payment);
    setCashTenderedInput('');
    if (stagedSubBill) closeStagedSubBill();
  };

  const handleBankTransferPayment = () => {
    if (amountDue <= 0) {
      console.log('💳 Amount due is 0, order already paid');
      return;
    }
    const payAmount = Math.round((stagedSubBill ? stagedTotal : amountDue) * 100) / 100;
    const payment: Payment = {
      id: generatePaymentId(),
      method: 'account2account',
      amount: payAmount,
      timestamp: Date.now(),
      ...(subBillPaymentMeta() ?? {}),
    };
    onProcessPayment(order.id, payment);
    if (stagedSubBill) closeStagedSubBill();
  };

  const handleCardPayment = async (amountToCharge: number, tipAmount: number = 0) => {
    // Validate amount - $0.00 is not allowed
    if (amountToCharge <= 0 || amountDue <= 0) {
      if (amountDue <= 0) {
        console.log('💳 Amount due is 0, order already paid');
        return;
      }
      alert('Payment amount must be greater than $0.00. If the order is already paid, no payment is needed.');
      return;
    }
    
    const terminal = terminals.find(t => t.id === selectedTerminalId);
    if (!terminal) {
        alert("Please select a valid payment terminal.");
        return;
    }
    
    if (!terminal.isPaired) {
        alert("This terminal is not paired. Please pair it in Settings first.");
        return;
    }
    
    setCardStatus('processing');
    setCardStatusMessage(`Please follow instructions on ${terminal.name}...`);

    // Import transaction state manager
    const { transactionStateManager } = await import('../lib/transactionStateManager');
    const { sendPaymentToTerminal } = await import('../lib/smartconnect');
    
    // Calculate total amount including tip
    const totalAmount = Math.round((amountToCharge + tipAmount) * 100) / 100;
    
    // Create pending transaction for tracking (power failure recovery)
    // Note: pollingUrl will be empty initially, updated by smartconnect service
    let pendingTransaction = transactionStateManager.createPendingTransaction(
      order.id,
      terminal,
      '', // Will be updated when we get polling URL from createTransaction
      totalAmount,
      tipAmount > 0 ? tipAmount : undefined
    );

    let isCancelled = false;

    try {
      // Handle delayed transaction callback (prevent showing multiple times, like HTML example)
      let delayedShown = false;
      const onDelayed = () => {
        if (!delayedShown) {
          delayedShown = true;
          setCardStatusMessage('Transaction is taking longer than usual. Please wait...');
        }
      };

      // Check for cancellation before starting
      const checkCancellation = () => {
        const updated = transactionStateManager.getPendingTransaction(pendingTransaction.id);
        return updated?.status === 'cancelled';
      };
      
      // Start transaction with state tracking
      const result = await sendPaymentToTerminal(
        terminal, 
        totalAmount,
        order,
        integrationSettings?.businessInfo,
        undefined, // userId - could be passed from currentUser if available
        undefined, // userName - could be passed from currentUser if available
        onDelayed,  // delayed callback
        pendingTransaction.id // transaction state ID for tracking
      );
      
      // Check if transaction was cancelled
      if (checkCancellation()) {
        isCancelled = true;
        setCardStatus('declined');
        setCardStatusMessage('Transaction was cancelled');
        return;
      }
      
      setCardStatusMessage(result.message);

      if (result.success) {
          setCardStatus('approved');
          
          // Log transaction ID for debugging
          console.log('💳 Card payment successful:', {
            orderId: order.id,
            amount: totalAmount,
            transactionId: result.transactionId,
            resultKeys: result ? Object.keys(result) : [],
            fullResult: result
          });
          
          if (!result.transactionId) {
            console.warn('⚠️ WARNING: Card payment succeeded but no transaction ID returned!', {
              orderId: order.id,
              amount: totalAmount,
              result
            });
          }
          
          const payment: Payment = {
              id: generatePaymentId(),
              method: 'card',
              amount: totalAmount,
              tip: tipAmount > 0 ? tipAmount : undefined,
              timestamp: Date.now(),
              terminalId: terminal.registerId,
              transactionId: result.transactionId,
              registerId: terminal.registerId,
              ...(subBillPaymentMeta() ?? {}),
          };
          onProcessPayment(order.id, payment);
          if (stagedSubBill) closeStagedSubBill();
      } else {
          // Handle cancellation outcome
          if (result.outcome === 3) { // TransactionOutcome.Cancelled
            setCardStatus('declined');
            setCardStatusMessage('Transaction cancelled by customer');
          } else {
            setCardStatus('declined');
          }
      }
    } catch (error: any) {
      const { logger } = await import('../lib/logger');
      logger.error('Payment error', { 
        orderId: order.id,
        amount: amountToCharge,
        error: error.message || 'Payment failed'
      });
      
      // Check if it was a cancellation
      if (error.message?.includes('cancelled') || error.message?.includes('CANCELLED')) {
        setCardStatus('declined');
        setCardStatusMessage('Transaction cancelled by customer');
      } else if (error.message?.includes('$0.00') || error.message?.includes('zero')) {
        setCardStatus('idle');
        setCardStatusMessage('');
        alert(error.message);
      } else {
        setCardStatus('declined');
        setCardStatusMessage(`Error: ${error.message || 'Payment failed'}`);
      }
    }
  };

  const handleSplitCashPayment = () => {
    const cashAmount = Math.round(splitCashAmountValue * 100) / 100;
    if (isNaN(cashAmount) || cashAmount <= 0 || cashAmount > amountDue) {
        alert("Please enter a valid cash amount that is less than or equal to the amount due.");
        return;
    }
    const payment: Payment = {
        id: generatePaymentId(),
        method: 'cash',
        amount: cashAmount,
        timestamp: Date.now(),
    };
    onProcessPayment(order.id, payment);
    setSplitCashAmountInput('');
  };
  
  const handleSendEmail = async () => {
      if (!/^\S+@\S+\.\S+$/.test(email)) {
          alert("Please enter a valid email address.");
          return;
      }

      try {
          setEmailSent(false);
          const { emailService } = await import('../lib/email');
          const customerName = customer?.name || 'Valued Customer';
          const customerLoyaltyPoints = customer?.loyaltyPoints || 0;
          
          const result = await emailService.sendOrderReceipt(email, order, customerName, integrationSettings?.businessInfo, customerLoyaltyPoints);
          
          if (result.success) {
              setEmailSent(true);
              setTimeout(() => setEmailSent(false), 5000);
          } else {
              alert(`Failed to send email: ${result.error || 'Unknown error'}`);
          }
      } catch (error: any) {
          console.error('Error sending receipt email:', error);
          alert(`Failed to send email: ${error.message || 'Unknown error'}`);
      }
  };

  const TabButton: React.FC<{ type: PaymentTab; icon: React.FC<any>; label: string; disabled?: boolean; title?: string }> = ({
    type,
    icon: Icon,
    label,
    disabled,
    title,
  }) => (
    <button
      type="button"
      onClick={() => {
        if (disabled) return;
        closeKeyboard();
        setActiveTab(type);
      }}
      disabled={disabled}
      title={title}
      className={`flex-1 flex flex-col items-center justify-center p-3 sm:p-4 border-b-4 transition-colors ${
        activeTab === type
          ? 'border-pink-500 text-pink-600'
          : disabled
            ? 'border-transparent text-gray-300 cursor-not-allowed'
            : 'border-transparent text-gray-500 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-7 h-7 sm:w-8 sm:h-8 mb-1" />
      <span className="font-semibold text-xs sm:text-sm">{label}</span>
    </button>
  );

  // Closed sub-bills strip data
  const closedSubBills = useMemo<SubBill[]>(() => order.subBills ?? [], [order.subBills]);

  const renderCardStatusIndicator = () => {
    const isFailure = cardStatus === 'declined';
    const isProcessing = cardStatus === 'processing';
    
    if (cardStatus === 'idle' || cardStatus === 'approved') return null;

    const Icon = isFailure ? XCircleIcon : ArrowPathIcon;
    const color = isFailure ? 'text-red-500' : 'text-blue-500';
    
    return (
        <div className={`text-center p-6 my-4 rounded-lg ${isFailure ? 'bg-red-50' : 'bg-blue-50'}`}>
            <Icon className={`w-12 h-12 mx-auto mb-2 ${color} ${isProcessing ? 'animate-spin' : ''}`} />
            <p className={`font-semibold ${color}`}>{cardStatusMessage}</p>
            {isProcessing && (
                <button 
                  onClick={async () => {
                    // Cancel the transaction
                    const { transactionStateManager } = await import('../lib/transactionStateManager');
                    const pending = transactionStateManager.getPendingTransactionsForOrder(order.id);
                    if (pending.length > 0) {
                      // Mark the most recent pending transaction as cancelled
                      const latest = pending.sort((a, b) => b.timestamp - a.timestamp)[0];
                      transactionStateManager.cancelTransaction(latest.id);
                    }
                    setCardStatus('idle');
                    setCardStatusMessage('');
                  }} 
                  className="mt-4 text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel Transaction
                </button>
            )}
            {isFailure && (
                <button onClick={() => setCardStatus('idle')} className="mt-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors py-2 px-4 flex items-center mx-auto">
                    <ArrowPathIcon className="w-5 h-5 mr-2"/>
                    Try Again
                </button>
            )}
        </div>
    );
  };

  // Payment status tracking - no sensitive data logged
  // When a payment is processed and there is still an outstanding balance,
  // reset the card status back to 'idle' so the user can run another card.
  useEffect(() => {
    if (cardStatus === 'approved' && amountDue > 0) {
      setCardStatus('idle');
      setCardStatusMessage('');
      // Clear inputs for the next card payment
      setCardAmountInput('');
      setCardTipAmount(0);
    }
  }, [(order.payments ?? []).length, amountDue, cardStatus]);

  // Show success screen only when there is nothing left to pay (amountDue <= 0).
  // If there is a balance due, always show the payment form even when paymentStatus is 'paid'.
  if (amountDue <= 0) {
      return (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto text-center p-8 my-auto">
                  <CheckCircleIcon className="w-16 h-16 mx-auto mb-4 text-green-500"/>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {isFullyPaidButUnsynced ? 'Order Already Paid' : 'Payment Successful!'}
                  </h2>
                  {order.orderType === 'event' && eventTokenDisplay && (
                    <p className="text-lg font-extrabold text-pink-600 mb-3">
                      Token number: {eventTokenDisplay.replace(/^Token /, '')}
                    </p>
                  )}
                  {isFullyPaidButUnsynced && (
                    <p className="text-gray-600 mb-4">This order has already been fully paid. Syncing status so it no longer appears in Pending.</p>
                  )}
                  
                  <div className="text-left mb-6">
                      <label htmlFor="emailReceipt" className="block text-sm font-medium text-gray-700 mb-1">
                        {order.orderType === 'event' ? 'Email receipt to customer (optional)' : 'Email Receipt (Optional)'}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          id="emailReceipt"
                          value={email}
                          readOnly={softKeyboardEnabled}
                          onFocus={() =>
                            openKeyboard({
                              id: 'receipt-email',
                              type: 'alphanumeric',
                              value: email,
                              label: 'Email',
                              onChange: setEmail,
                            })
                          }
                          onChange={(e) => {
                            if (!softKeyboardEnabled) setEmail(e.target.value);
                          }}
                          placeholder={customer ? `${customer.name}'s email` : 'customer@example.com'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        />
                        <button onClick={handleSendEmail} className="bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors px-4">Send</button>
                      </div>
                      {emailSent && <p className="text-green-600 text-sm mt-1">Receipt sent to {email}!</p>}
                  </div>

                  <div className="space-y-3">
                      {order.orderType !== 'event' && (
                        <button onClick={() => onPrintReceipt(order)} className="w-full bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors py-3 font-bold text-lg flex items-center justify-center">
                            <PrinterIcon className="w-5 h-5 mr-2"/> Print Receipt
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowReceiptQr(true)}
                        className="w-full bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors py-3 font-bold text-lg"
                      >
                        Show customer receipt QR
                      </button>
                      {order.orderType === 'event' && onMarkOrderCompleted && (
                        <button
                          onClick={async () => {
                            await onMarkOrderCompleted(order);
                            onNewOrder();
                          }}
                          className="w-full bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors py-3 font-bold text-lg"
                        >
                          Mark as completed &amp; close
                        </button>
                      )}
                       <button onClick={onNewOrder} className="w-full bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors py-3 font-bold text-lg">
                          New Order
                      </button>
                  </div>
              </div>
              {showReceiptQr && (
                <ReceiptQrModal
                  order={order}
                  businessInfo={integrationSettings?.businessInfo}
                  onClose={() => setShowReceiptQr(false)}
                />
              )}
          </div>
      );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden my-auto">
        <header className="p-4 sm:p-6 border-b text-center relative flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800">Process Payment</h2>
          {stagedSubBill ? (
            <>
              <p className="text-gray-600">
                Sub-bill total{stagedSubBill.customerName ? ` for ${stagedSubBill.customerName}` : ''}
              </p>
              <p className="text-4xl sm:text-5xl font-extrabold text-pink-500">${stagedTotal.toFixed(2)}</p>
              <p className="mt-1 text-xs text-gray-500">Order balance after this: ${(amountDue - stagedTotal).toFixed(2)}</p>
            </>
          ) : (
            <>
              <p className="text-gray-600">Total Amount Due</p>
              <p className="text-4xl sm:text-5xl font-extrabold text-pink-500">${amountDue.toFixed(2)}</p>
            </>
          )}
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </header>

        {/* Closed sub-bills strip — appears once 1+ sub-bills have been settled. */}
        {closedSubBills.length > 0 && (
          <div className="px-3 py-2 sm:px-4 border-b bg-green-50 flex-shrink-0">
            <p className="text-[11px] uppercase font-semibold text-green-800 mb-1">Sub-bills paid so far ({closedSubBills.length})</p>
            <ul className="space-y-1">
              {closedSubBills.map((sb) => {
                const itemCount = sb.itemAllocations.reduce((s, a) => s + a.quantity, 0);
                return (
                  <li key={sb.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-green-900 truncate">
                      <strong>{sb.name || 'Guest'}</strong> · {itemCount} item{itemCount === 1 ? '' : 's'} · ${sb.total.toFixed(2)}
                    </span>
                    {onReprintSubBill && (
                      <button
                        type="button"
                        onClick={() => onReprintSubBill(order, sb)}
                        className="text-green-700 hover:text-green-900 font-semibold text-[11px] flex items-center gap-1 flex-shrink-0"
                      >
                        <PrinterIcon className="w-3 h-3" /> Reprint
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Staged sub-bill banner — shown while cashier is choosing a payment method for the staged items. */}
        {stagedSubBill && (
          <div className="px-3 py-2 sm:px-4 border-b bg-amber-50 flex items-center justify-between gap-2 flex-shrink-0">
            <div className="text-xs text-amber-900 min-w-0">
              <p className="font-semibold truncate">
                Sub-bill staged{stagedSubBill.customerName ? ` for ${stagedSubBill.customerName}` : ''} — ${stagedTotal.toFixed(2)}
              </p>
              <p className="truncate">
                {stagedSubBill.allocations.reduce((s, a) => s + a.quantity, 0)} item(s) · pick a payment method below
              </p>
            </div>
            <button
              type="button"
              onClick={closeStagedSubBill}
              className="text-amber-800 hover:text-amber-950 underline font-semibold text-[11px] flex-shrink-0"
            >
              Cancel sub-bill
            </button>
          </div>
        )}

        <div className={`grid ${subBillsAllowed ? 'grid-cols-5' : 'grid-cols-4'} border-b flex-shrink-0`}>
            <TabButton type="card" icon={CreditCardIcon} label="Card" />
            <TabButton type="cash" icon={CashIcon} label="Cash" />
            <TabButton type="bank" icon={BankBuildingIcon} label="Bank" />
            <TabButton
              type="split"
              icon={ColumnsIcon}
              label="Split"
              disabled={!!stagedSubBill}
              title={stagedSubBill ? 'Cancel the staged sub-bill first' : undefined}
            />
            {subBillsAllowed && (
              <TabButton
                type="items"
                icon={UsersIcon}
                label="Items"
                disabled={!!stagedSubBill}
                title={stagedSubBill ? 'Cancel the staged sub-bill first' : 'Pay for selected items only'}
              />
            )}
        </div>
        {onCancelUnpaidOrder && order.paymentStatus === 'unpaid' && order.orderType === 'event' && (
          <div className="px-4 py-2 border-b bg-red-50 text-center flex-shrink-0">
            <button
              type="button"
              onClick={() => void onCancelUnpaidOrder()}
              className="text-sm font-semibold text-red-800 hover:text-red-950 underline"
            >
              Cancel entire order (void ticket)
            </button>
          </div>
        )}
        <main className="p-4 sm:p-6 flex-1 min-h-0 overflow-y-auto">
          {activeTab === 'items' && subBillsAllowed && (
            <SubBillBuilder
              order={order}
              onCancel={() => setActiveTab('card')}
              onPay={(result) => {
                setStagedSubBill(result);
                // Pre-fill amount inputs for card/cash/bank with the sub-bill total.
                setCardAmountInput(result.total.toFixed(2));
                setCashTenderedInput(result.total.toFixed(2));
                setActiveTab('card');
              }}
              onPrintBillOnly={
                onPrintSubBillPreview
                  ? (result) =>
                      onPrintSubBillPreview(order, {
                        name: result.customerName.trim() || 'Guest',
                        total: result.total,
                        allocations: result.allocations,
                        discountAllocationMode: result.discountAllocationMode,
                      })
                  : undefined
              }
            />
          )}
          {activeTab === 'cash' && (
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {stagedSubBill ? `Amount Tendered (sub-bill total $${stagedTotal.toFixed(2)})` : 'Amount Tendered (partial payments allowed)'}
                    </label>
                    <div className="flex gap-2 mb-2">
                        <button onClick={() => setCashTenderedInput(effectivePayAmount.toFixed(2))} className="bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors flex-1 py-2">Exact</button>
                        {effectivePayAmount < 50 && <button onClick={() => setCashTenderedInput('50.00')} className="bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors flex-1 py-2">$50</button>}
                        {effectivePayAmount < 100 && <button onClick={() => setCashTenderedInput('100.00')} className="bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors flex-1 py-2">$100</button>}
                    </div>
                    <input 
                        type="text"
                        inputMode="decimal"
                        step="0.01"
                        pattern="^[0-9]*[.,]?[0-9]*$"
                        value={cashTenderedInput}
                        readOnly={softKeyboardEnabled}
                        onFocus={() =>
                          openKeyboard({
                            id: 'payment-cash',
                            type: 'numeric',
                            value: cashTenderedInput,
                            label: 'Amount Tendered',
                            onChange: setCashTenderedInput,
                          })
                        }
                        onChange={(e) => {
                            if (softKeyboardEnabled) return;
                            const value = e.target.value;
                            if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                setCashTenderedInput(value);
                            }
                        }} 
                        placeholder={effectivePayAmount.toFixed(2)} 
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-xl p-3 text-center"
                    />
                </div>
                 {cashTenderedInput !== '' && cashTenderedValue > 0 && (
                    <div className="text-center bg-gray-100 p-3 rounded-md space-y-1">
                        {cashTenderedValue >= effectivePayAmount ? (
                          <>
                            <p className="text-sm text-gray-600">Change Due</p>
                            <p className="text-2xl font-bold text-green-600">${(Math.round((cashTenderedValue - effectivePayAmount) * 100) / 100).toFixed(2)}</p>
                          </>
                        ) : stagedSubBill ? (
                          <>
                            <p className="text-sm text-red-600">Not enough — sub-bill needs ${stagedTotal.toFixed(2)}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-600">Partial payment – amount to apply</p>
                            <p className="text-2xl font-bold text-green-600">${Math.min(cashTenderedValue, amountDue).toFixed(2)}</p>
                            <p className="text-xs text-gray-500 mt-1">${(amountDue - Math.min(cashTenderedValue, amountDue)).toFixed(2)} still due after</p>
                          </>
                        )}
                    </div>
                )}
                <button 
                  onClick={handleCashPayment} 
                  disabled={amountDue <= 0 || cashTenderedValue <= 0 || (!!stagedSubBill && cashTenderedValue + 0.005 < stagedTotal)} 
                  className="w-full bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed py-3 font-bold text-lg"
                >
                  {amountDue <= 0
                    ? 'Order Already Paid'
                    : stagedSubBill
                      ? `Confirm $${stagedTotal.toFixed(2)} Cash for sub-bill`
                      : cashTenderedValue >= amountDue
                        ? 'Confirm Cash Payment'
                        : `Apply $${Math.min(cashTenderedValue, amountDue).toFixed(2)} Cash`}
                </button>
            </div>
          )}
          {activeTab === 'bank' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Record {stagedSubBill ? 'this sub-bill' : 'full payment'} as <strong>Bank transfer</strong> when the customer has already paid (or will pay) via bank deposit — no card terminal charge.
              </p>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Amount to record</p>
                <p className="text-3xl font-extrabold text-pink-600">${effectivePayAmount.toFixed(2)}</p>
              </div>
              <button
                type="button"
                onClick={handleBankTransferPayment}
                disabled={amountDue <= 0}
                className="w-full bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed py-3 font-bold text-lg"
              >
                Confirm bank transfer (${effectivePayAmount.toFixed(2)})
              </button>
            </div>
          )}
          {activeTab === 'card' && (
            <div>
              {cardStatus === 'idle' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Terminal
                    </label>
                    <select
                      value={selectedTerminalId}
                      onChange={(e) => setSelectedTerminalId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 p-3"
                    >
                      {terminals.length > 0 ? (
                        terminals.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}{t.isPaired ? ' (paired)' : ''}
                          </option>
                        ))
                      ) : (
                        <option disabled>No terminals configured</option>
                      )}
                    </select>
                    {terminalSelectionNotice ? (
                      <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                        {terminalSelectionNotice}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {stagedSubBill ? `Card Amount (locked to sub-bill total)` : 'Card Amount (optional)'}
                    </label>
                    {!stagedSubBill && (
                      <>
                        <div className="flex gap-2 mb-2">
                          <button
                            onClick={() => setCardAmountInput(amountDue.toFixed(2))}
                            className="bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors flex-1 py-2 text-sm"
                          >
                            Pay Full Amount
                          </button>
                        </div>
                        <div className="mb-2">
                          <label className="block text-xs text-gray-600 mb-1">Split between people (equal):</label>
                          <div className="grid grid-cols-5 gap-2">
                            {[2, 3, 4, 5, 6].map((numPeople) => {
                              const splitAmount = Math.round((amountDue / numPeople) * 100) / 100;
                              return (
                                <button
                                  key={numPeople}
                                  onClick={() =>
                                    setCardAmountInput(splitAmount.toFixed(2))
                                  }
                                  className="bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors py-2 text-xs"
                                  title={`Split ${splitAmount.toFixed(2)} per person`}
                                >
                                  {numPeople} ways
                                </button>
                              );
                            })}
                          </div>
                          <p className="mt-1 text-[11px] text-gray-500">
                            Need to split by item instead? Use the <strong>Items</strong> tab.
                          </p>
                        </div>
                      </>
                    )}
                    <input
                      type="text"
                      inputMode="decimal"
                      step="0.01"
                      pattern="^[0-9]*[.,]?[0-9]*$"
                      value={cardAmountInput}
                      readOnly={softKeyboardEnabled || !!stagedSubBill}
                      onFocus={() => {
                        if (stagedSubBill) return;
                        openKeyboard({
                          id: 'payment-card-amount',
                          type: 'numeric',
                          value: cardAmountInput,
                          label: 'Card Amount',
                          onChange: setCardAmountInput,
                        });
                      }}
                      onChange={(e) => {
                        if (softKeyboardEnabled) return;
                        const value = e.target.value;
                        if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                          setCardAmountInput(value);
                        }
                      }}
                      placeholder={amountDue.toFixed(2)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-xl p-3 text-center"
                    />
                    {!stagedSubBill && (
                      <p className="mt-1 text-xs text-gray-500">
                        You can take multiple card payments. Each payment will reduce the remaining
                        balance.
                      </p>
                    )}
                    {!stagedSubBill && cardAmountInput && (cardAmountValue <= 0 || cardAmountValue > amountDue) && (
                      <p className="mt-1 text-xs text-red-500">
                        Amount must be greater than $0.00 and not more than the remaining due.
                      </p>
                    )}
                  </div>

                  <TipInput
                    baseAmount={Math.round((stagedSubBill ? stagedTotal : amountDue) * 100) / 100}
                    tipAmount={cardTipAmount}
                    onTipChange={setCardTipAmount}
                    className="mb-4"
                  />

                  <button
                    onClick={() => {
                      const amount = stagedSubBill ? stagedTotal : cardAmountValue;
                      handleCardPayment(amount, cardTipAmount);
                    }}
                    disabled={
                      !selectedTerminalId ||
                      terminals.length === 0 ||
                      (stagedSubBill ? stagedTotal <= 0 : cardAmountValue <= 0 || cardAmountValue > amountDue)
                    }
                    className="w-full bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed py-3 font-bold text-lg"
                  >
                    {(() => {
                      const baseAmount = stagedSubBill ? stagedTotal : cardAmountValue;
                      if (cardTipAmount > 0) {
                        return `Pay $${(Math.round((baseAmount + cardTipAmount) * 100) / 100).toFixed(2)} (${baseAmount.toFixed(2)} + ${cardTipAmount.toFixed(2)} tip)`;
                      }
                      if (stagedSubBill) {
                        return `Pay $${stagedTotal.toFixed(2)} by Card for sub-bill`;
                      }
                      return `Pay $${cardAmountValue.toFixed(2) || amountDue.toFixed(2)} by Card`;
                    })()}
                  </button>
                </div>
              ) : (
                renderCardStatusIndicator()
              )}
            </div>
          )}
          {activeTab === 'split' && (
             <div className="space-y-6">
                <div>
                    <h3 className="font-semibold text-gray-700 mb-2">1. Pay with Cash</h3>
                    <div className="flex gap-4">
                        <input 
                            type="text" 
                            inputMode="decimal"
                            step="0.01"
                            pattern="^[0-9]*[.,]?[0-9]*$"
                            value={splitCashAmountInput} 
                            readOnly={softKeyboardEnabled}
                            onFocus={() =>
                              openKeyboard({
                                id: 'payment-split-cash',
                                type: 'numeric',
                                value: splitCashAmountInput,
                                label: 'Cash Amount',
                                onChange: setSplitCashAmountInput,
                              })
                            }
                            onChange={(e) => {
                                if (softKeyboardEnabled) return;
                                const value = e.target.value;
                                if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                    setSplitCashAmountInput(value);
                                }
                            }} 
                            placeholder="Enter cash amount" 
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500 p-3" 
                        />
                        <button onClick={handleSplitCashPayment} disabled={!splitCashAmountInput || splitCashAmountValue <= 0} className="bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors py-3 px-6">Process Cash</button>
                    </div>
                </div>
                <div>
                    <h3 className="font-semibold text-gray-700 mb-2">2. Pay Remaining with Card</h3>
                    <p className="text-center text-gray-500 mb-2">Remaining: <span className="font-bold">${(Math.round(amountDue * 100) / 100).toFixed(2)}</span></p>
                     {cardStatus === 'idle' ? (
                        <>
                            <select value={selectedTerminalId} onChange={(e) => setSelectedTerminalId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 p-3 mb-4">
                                {terminals.length > 0 ? (
                                  terminals.map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {t.name}
                                    </option>
                                  ))
                                ) : (
                                  <option disabled>No terminals available</option>
                                )}
                            </select>
                            <TipInput
                              baseAmount={Math.round(amountDue * 100) / 100}
                              tipAmount={cardTipAmount}
                              onTipChange={setCardTipAmount}
                              className="mb-4"
                            />
                            <button onClick={() => handleCardPayment(Math.round(amountDue * 100) / 100, cardTipAmount)} disabled={amountDue <= 0 || !selectedTerminalId || terminals.length === 0} className="w-full bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed py-3 font-bold text-lg">
                              {cardTipAmount > 0 
                                ? `Pay $${(Math.round((amountDue + cardTipAmount) * 100) / 100).toFixed(2)} (${amountDue.toFixed(2)} + ${cardTipAmount.toFixed(2)} tip)`
                                : 'Pay Remaining by Card'
                              }
                            </button>
                        </>
                    ) : renderCardStatusIndicator() }
                </div>
             </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PaymentModal;